import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { ApprovalFlowDisplayComponent } from '../shared/approval-flow-display/approval-flow-display.component';
import { FormFieldConfig } from '../dynamic-form/dynamic-form/dynamic-form.component';

@Component({
  selector: 'app-form-display',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ApprovalFlowDisplayComponent],
  templateUrl: './form-display.component.html',
  styleUrls: ['./form-display.component.scss']
})
export class FormDisplayComponent implements OnInit {
  @Input() config: FormFieldConfig[] = [];
  @Input() formData: any = {};
  @Input() formName: string = '';

  constructor() {}

  ngOnInit(): void {
    // 初始化邏輯
  }

  /**
   * 格式化日期時間顯示
   */
  formatDateTime(dateInput: any): string {
    if (!dateInput) return '';
    
    let date: Date;
    
    if (typeof dateInput === 'number') {
      // 判斷是否為秒級時間戳
      if (dateInput < 10000000000) {
        date = new Date(dateInput * 1000);
      } else {
        date = new Date(dateInput);
      }
    } else if (typeof dateInput === 'string') {
      const numericValue = parseInt(dateInput);
      if (!isNaN(numericValue) && numericValue < 10000000000) {
        date = new Date(numericValue * 1000);
      } else {
        date = new Date(dateInput);
      }
    } else {
      return '';
    }

    // 檢查日期是否有效
    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  /**
   * 格式化日期顯示
   */
  formatDate(dateInput: any): string {
    if (!dateInput) return '';
    
    let date: Date;
    
    if (typeof dateInput === 'number') {
      // 判斷是否為秒級時間戳
      if (dateInput < 10000000000) {
        date = new Date(dateInput * 1000);
      } else {
        date = new Date(dateInput);
      }
    } else if (typeof dateInput === 'string') {
      const numericValue = parseInt(dateInput);
      if (!isNaN(numericValue) && numericValue < 10000000000) {
        date = new Date(numericValue * 1000);
      } else {
        date = new Date(dateInput);
      }
    } else {
      return '';
    }

    // 檢查日期是否有效
    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  /**
   * 取得欄位的值
   */
  getFieldValue(fieldName: string): any {
    if (!this.formData) return null;
    
    // 直接檢查欄位值
    if (this.formData.hasOwnProperty(fieldName)) {
      const value = this.formData[fieldName];
      
      // 如果是物件且有 value 屬性，則取得 value
      if (value && typeof value === 'object' && value.hasOwnProperty('value')) {
        return value.value;
      }
      
      // 否則直接返回值
      return value;
    }
    
    return null;
  }

  /**
   * 取得顯示文字
   */
  getDisplayValue(value: any): string {
    if (value === null || value === undefined) return '';
    
    // 如果是物件
    if (typeof value === 'object') {
      // 如果有 value 屬性，嘗試顯示該值
      if (value.hasOwnProperty('value')) {
        return this.getDisplayValue(value.value);
      }
      
      // 如果是陣列，嘗試顯示為字串
      if (Array.isArray(value)) {
        return value.map(item => this.getDisplayValue(item)).join(', ');
      }
      
      // 其他物件，嘗試找到可顯示的屬性
      if (value.name) return value.name;
      if (value.label) return value.label;
      if (value.title) return value.title;
      if (value.text) return value.text;
      
      // 最後才使用 JSON.stringify
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return '[Object]';
      }
    }
    
    return String(value);
  }

  /**
   * 解析照片資料
   */
  parsePhotos(photosData: any): any[] {
    if (!photosData) return [];
    
    if (typeof photosData === 'string') {
      try {
        return JSON.parse(photosData);
      } catch {
        return [];
      }
    }
    
    if (Array.isArray(photosData)) {
      return photosData;
    }
    
    return [];
  }

  /**
   * 取得Google Maps連結
   */
  getGoogleMapsUrl(location: any): string {
    if (!location) return '#';
    
    let lat, lng;
    
    if (typeof location === 'string') {
      try {
        const parsed = JSON.parse(location);
        lat = parsed.lat || parsed.latitude;
        lng = parsed.lng || parsed.longitude;
      } catch {
        return '#';
      }
    } else if (typeof location === 'object') {
      lat = location.lat || location.latitude;
      lng = location.lng || location.longitude;
    }
    
    if (lat && lng) {
      return `https://www.google.com/maps?q=${lat},${lng}`;
    }
    
    return '#';
  }

  /**
   * 取得多選項目的選中文字
   */
  getMultiCheckboxSelectedText(value: any): string {
    if (!value) return '';
    
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.join(', ');
        }
        return value;
      } catch {
        return value;
      }
    }
    
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    
    return String(value);
  }

  /**
   * 轉換為簽核流程步驟
   */
  convertToApprovalFlowSteps(field: FormFieldConfig): any[] {
    const value = this.getFieldValue(field.name);
    if (!value) return [];
    
    try {
      // 處理簽核流程資料的多種可能結構
      let approvalData: any = value;
      
      // 如果是字串，嘗試解析
      if (typeof value === 'string') {
        try {
          approvalData = JSON.parse(value);
        } catch {
          return [];
        }
      }
      
      // 嘗試從不同的資料結構中提取步驟
      if (approvalData.value?.steps) {
        // 雙層巢狀結構
        return approvalData.value.steps;
      } else if (approvalData.steps) {
        // 單層結構
        return approvalData.steps;
      } else if (Array.isArray(approvalData)) {
        // 直接是陣列
        return approvalData;
      }
    } catch (error) {
      console.warn('解析簽核流程步驟失敗:', error);
    }
    
    return [];
  }

  /**
   * 取得簽核流程資料
   */
  getApprovalFlowData(field: FormFieldConfig): any {
    const value = this.getFieldValue(field.name);
    return value;
  }

  /**
   * 檢查是否有檔案
   */
  hasFiles(field: FormFieldConfig): boolean {
    const value = this.getFieldValue(field.name);
    if (!value) return false;
    
    // 檢查不同的檔案資料結構
    if (value.files && Array.isArray(value.files) && value.files.length > 0) {
      return true;
    }
    
    // 檢查是否直接是檔案陣列
    if (Array.isArray(value) && value.length > 0) {
      return true;
    }
    
    // 檢查欄位配置中的檔案
    if (field.files && Array.isArray(field.files) && field.files.length > 0) {
      return true;
    }
    
    return false;
  }

  /**
   * 取得檔案資料
   */
  getFiles(field: FormFieldConfig): any[] {
    const value = this.getFieldValue(field.name);
    
    // 優先檢查值中的檔案
    if (value) {
      if (value.files && Array.isArray(value.files)) {
        return value.files;
      }
      
      if (Array.isArray(value)) {
        return value;
      }
    }
    
    // 檢查欄位配置中的檔案
    if (field.files && Array.isArray(field.files)) {
      return field.files;
    }
    
    return [];
  }

  /**
   * 檢視檔案
   */
  viewFile(file: any): void {
    if (!file) return;
    
    // 嘗試不同的 URL 屬性
    const url = file.url || file.fileUrl || file.downloadUrl || file.src || file.href;
    
    if (url) {
      window.open(url, '_blank');
    } else {
      console.warn('無法找到檔案 URL:', file);
    }
  }

  /**
   * 檢視圖片全尺寸
   */
  viewImageFullSize(imageUrl: string): void {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  }

  /**
   * 簽核完成處理
   */
  onApprovalComplete(event: any): void {
    // 在顯示模式下不需要處理簽核完成事件
    console.log('Approval complete event in display mode:', event);
  }

  /**
   * TrackBy 函數用於優化 ngFor 性能
   */
  trackByFieldName(index: number, field: FormFieldConfig): string {
    return field.name || field.id || index.toString();
  }
}